import { ajaxSetup } from 'cadenza/api-client/api';
import { PARAMETER_EMBEDDING_TARGET_NAME } from 'cadenza/api-client/api-constants';
import type { RepositoryName } from 'cadenza/repository/model/repository-name.gen';
import type { WorkbookId } from 'cadenza/repository/model/workbook-id.gen';

/**
 * Set up url params for all subsequent ajax requests. Should be called as soon as possible
 * so that no request misses necessary parameters. After calling this all requests will use
 * development versions of objecttypes.
 * Note: For defaults ajax params like below we don't send the params at all:
 * - defaultRepositoryPublicationStage="published"
 * - developmentRepository=""
 */
export function setupAjaxForDevelopmentVersion(workbookId?: WorkbookId) {
  ajaxSetup({ defaultRepositoryPublicationStage: 'DEVELOPMENT', ...getAjaxParamForWorkbookId(workbookId) });
}

/**
 * After calling this all requests will use published versions of objecttypes except for the repository
 * with names passed here. If the opened workbook version is published, then we don't want to set
 * developmentRepository for next requests, because we want all OTs to be loaded
 * in published version with no exceptions.
 * Note: For defaults ajax params like below we don't send the params at all:
 * - defaultRepositoryPublicationStage="published"
 * - developmentRepository=""
 */
function getAjaxParamForPublishedVersion(isPublished: boolean, repositoryName: RepositoryName | null) {
  if (repositoryName && !isPublished) {
    return { developmentRepository: repositoryName };
  }
}

export function setupAjaxForWorkbook(workbookId: WorkbookId, isPublished: boolean) {
  const embeddingTargetId = new URLSearchParams(location.search).get(PARAMETER_EMBEDDING_TARGET_NAME);
  ajaxSetup({
    ...getAjaxParamForWorkbookId(workbookId),
    ...getAjaxParamForPublishedVersion(isPublished, workbookId.repositoryName),
    ...(embeddingTargetId && { [PARAMETER_EMBEDDING_TARGET_NAME]: embeddingTargetId }),
  });
}

export function setupAjaxForWorkbookReport(workbookId: WorkbookId) {
  ajaxSetup({
    ...getAjaxParamForWorkbookId(workbookId),
  });
}

/**
 * WorkbookId is required for all endpoints executed via workbook to make sure that
 * project_variables defined in user base restrictions are resolved and user only sees that they need to see.
 *
 * @see net/disy/cadenza/accessmanager/web/ProjectIdProvider.java
 */
function getAjaxParamForWorkbookId(workbookId?: WorkbookId) {
  return workbookId && { repositoryName: workbookId.repositoryName, workbookId: workbookId.itemName };
}
